import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Users, FolderKanban, ShoppingCart, TrendingUp } from 'lucide-react'

interface DashboardStats {
  totalUsers: number
  totalProjects: number
  totalOrders: number
  totalVolume: number
  pendingProjects: number
  activeOrders: number
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalProjects: 0,
    totalOrders: 0,
    totalVolume: 0,
    pendingProjects: 0,
    activeOrders: 0,
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    try {
      // Fetch counts in parallel
      const [usersCount, projectsCount, ordersCount, volumeData, pendingProjectsCount, activeOrdersCount] =
        await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('projects').select('*', { count: 'exact', head: true }),
          supabase.from('orders').select('*', { count: 'exact', head: true }),
          supabase.from('trades').select('quantity_tons'),
          supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'pending'),
          supabase
            .from('orders')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'open'),
        ])

      // Calculate total volume
      const totalVolume = volumeData.data?.reduce((sum, trade) => sum + trade.quantity_tons, 0) || 0

      setStats({
        totalUsers: usersCount.count || 0,
        totalProjects: projectsCount.count || 0,
        totalOrders: ordersCount.count || 0,
        totalVolume,
        pendingProjects: pendingProjectsCount.count || 0,
        activeOrders: activeOrdersCount.count || 0,
      })
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-500',
      trend: null,
    },
    {
      title: 'Total Projects',
      value: stats.totalProjects,
      icon: FolderKanban,
      color: 'bg-green-500',
      badge: stats.pendingProjects > 0 ? `${stats.pendingProjects} pending` : null,
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: ShoppingCart,
      color: 'bg-purple-500',
      badge: stats.activeOrders > 0 ? `${stats.activeOrders} active` : null,
    },
    {
      title: 'Total Volume',
      value: `${stats.totalVolume.toLocaleString()} tons`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      trend: null,
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-primary"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Platform overview and key metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.title} className="admin-card">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              {stat.badge && (
                <span className="admin-badge-warning">{stat.badge}</span>
              )}
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </h3>
            <p className="text-sm text-gray-600">{stat.title}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Projects</h3>
          <p className="text-gray-600 text-sm mb-4">
            {stats.pendingProjects} project{stats.pendingProjects !== 1 ? 's' : ''} awaiting approval
          </p>
          <a
            href="/projects"
            className="text-admin-primary hover:text-admin-secondary font-medium text-sm"
          >
            Review projects →
          </a>
        </div>

        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Active Orders</h3>
          <p className="text-gray-600 text-sm mb-4">
            {stats.activeOrders} open order{stats.activeOrders !== 1 ? 's' : ''} in the order book
          </p>
          <a
            href="/orders"
            className="text-admin-primary hover:text-admin-secondary font-medium text-sm"
          >
            View orders →
          </a>
        </div>

        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">User Management</h3>
          <p className="text-gray-600 text-sm mb-4">
            Manage user accounts and permissions
          </p>
          <a
            href="/users"
            className="text-admin-primary hover:text-admin-secondary font-medium text-sm"
          >
            Manage users →
          </a>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="admin-card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Database</span>
            <span className="admin-badge-success">Operational</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Order Matching Engine</span>
            <span className="admin-badge-success">Operational</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">Smart Contracts</span>
            <span className="admin-badge-info">Testnet</span>
          </div>
        </div>
      </div>
    </div>
  )
}
